<template>
    <div class="post-detail-container" v-loading="loading">
        <div v-if="post" class="post-content-container">
            <div class="header">
                <el-page-header @back="$router.push('/forum')"
                    :content="post.type === 'question' ? '问题详情' : (post.type === 'moment' ? '新鲜事详情' : '文章详情')" />
            </div>

            <div class="post-main-content">
                <h1 class="post-title" v-if="post.type !== 'moment'">{{ post.title }}</h1>
                <div class="post-meta">
                    <div class="author-info" @click="goToUserSpace(post.author.id)" style="cursor: pointer;">
                        <el-avatar :size="32" :src="post.author.avatar || defaultAvatar" />
                        <div class="author-details">
                            <span class="author-name">{{ post.author.username }}</span>
                            <div class="post-meta-sub">
                                <span class="post-date">{{ formatDate(post.createdAt) }}</span>
                                <span class="ip-location" v-if="post.ipLocation">IP属地: {{ post.ipLocation }}</span>
                            </div>
                        </div>
                    </div>
                    <div class="post-stats">
                        <span v-if="isAuthor" class="action-item edit-link" @click="openEditPostDialog">
                            <el-icon>
                                <Edit />
                            </el-icon> 编辑
                        </span>
                        <span v-if="canDelete" class="action-item delete-link" @click="handleDeletePost">
                            <el-icon>
                                <Delete />
                            </el-icon> 删除
                        </span>
                        <span><el-icon>
                                <View />
                            </el-icon> {{ post.views }} 次浏览</span>
                    </div>
                </div>

                <el-divider />

                <div class="content-body">
                    <v-md-editor v-if="post.type !== 'moment'" :model-value="post.content" mode="preview" />
                    <div v-else>
                        <v-md-editor :model-value="formatMomentForEditor(post.content)" mode="preview" />

                        <!-- Image Grid for Moments -->
                        <div class="moment-image-grid" v-if="extractMomentImages(post.content).length > 0">
                            <div v-for="(img, imgIndex) in extractMomentImages(post.content)" :key="imgIndex"
                                class="moment-image-wrapper" @click.stop>
                                <el-image :src="img" :preview-src-list="extractMomentImages(post.content)"
                                    :initial-index="imgIndex" fit="cover" class="moment-image" hide-on-click-modal
                                    preview-teleported />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="post-footer-actions"
                    v-if="post.type === 'question' && (post.status === 'resolved' || (isAuthor && post.status !== 'resolved'))">
                    <el-divider />
                    <div v-if="isAuthor && post.status !== 'resolved'" class="resolve-btn-container">
                        <el-button type="success" size="large" @click="handleResolvePost" icon="Check" round>
                            该问题已得到解决
                        </el-button>
                        <p class="resolve-tip">点击标记为已解决，帮助他人快速找到答案</p>
                    </div>
                    <div v-else-if="post.status === 'resolved'" class="resolved-status-footer">
                        <el-tag type="success" effect="dark" size="large" class="resolved-tag-big">
                            <el-icon style="margin-right: 8px;">
                                <Check />
                            </el-icon>
                            该问题已得到解决
                        </el-tag>
                    </div>
                </div>
            </div>

            <div class="comments-section">
                <div class="comments-header">
                    <h2>{{ commentsCount }} 条{{ post.type === 'question' ? '回答' : '评论' }}</h2>
                </div>

                <!-- Add main comment form -->
                <div class="main-comment-form">
                    <el-input v-model="newComment" type="textarea" :rows="3"
                        :placeholder="post.type === 'question' ? '写下你的回答...' : '发表你的精彩评论...'" />
                    <div class="form-actions">
                        <el-button type="primary" :loading="submitting" @click="submitComment">发表评论</el-button>
                    </div>
                </div>

                <div class="comments-list">
                    <comment-item v-for="comment in commentTree" :key="comment.id" :comment="comment"
                        :replies="comment.replies" type="forum" :targetId="post.id"
                        @reply-submitted="fetchPostDetail" />
                </div>
            </div>
        </div>

        <!-- Edit Post Dialog -->
        <el-dialog v-model="editDialogVisible" title="编辑帖子" width="800px" :close-on-click-modal="false"
            destroy-on-close>
            <el-form :model="editForm" label-position="top">
                <el-form-item label="标题" v-if="post && post.type !== 'moment'">
                    <el-input v-model="editForm.title" placeholder="输入标题..." />
                </el-form-item>
                <el-form-item label="内容">
                    <v-md-editor v-model="editForm.content" height="400px" placeholder="输入内容..." />
                </el-form-item>
            </el-form>
            <template #footer>
                <span class="dialog-footer">
                    <el-button @click="editDialogVisible = false">取消</el-button>
                    <el-button type="primary" :loading="updating" @click="handleUpdatePost">保存更新</el-button>
                </span>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import api, { getUser } from '@/utils/api';
import dayjs from 'dayjs';
import CommentItem from '@/components/CommentItem.vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { View, Delete, Check, Edit } from '@element-plus/icons-vue';
import { useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const loading = ref(true);
const post = ref(null);
const rawComments = ref([]);
const newComment = ref('');
const submitting = ref(false);
const updating = ref(false);
const editDialogVisible = ref(false);
const editForm = ref({
    title: '',
    content: ''
});
const defaultAvatar = 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png';

const fetchPostDetail = async () => {
    try {
        const id = route.params.id;
        const response = await api.get(`/api/forum/posts/${id}`);
        post.value = response.data.post;
        rawComments.value = response.data.comments;
    } catch (error) {
        console.error('Failed to fetch post detail:', error);
        ElMessage.error('帖子获失败');
    } finally {
        loading.value = false;
    }
};

const commentTree = computed(() => {
    const items = JSON.parse(JSON.stringify(rawComments.value));
    const map = {};
    const tree = [];

    items.forEach(item => {
        item.replies = [];
        map[item.id] = item;
    });

    items.forEach(item => {
        if (item.parentId && map[item.parentId]) {
            map[item.parentId].replies.push(item);
        } else {
            tree.push(item);
        }
    });

    return tree;
});

const commentsCount = computed(() => rawComments.value.length);

const formatDate = (date) => {
    return dayjs(date).format('YYYY-MM-DD HH:mm');
};

const submitComment = async () => {
    if (!newComment.value.trim()) return;
    submitting.value = true;
    try {
        await api.post(`/api/forum/posts/${post.value.id}/comments`, {
            content: newComment.value,
            isAnswer: post.value.type === 'question'
        });
        ElMessage.success('评论成功');
        newComment.value = '';
        fetchPostDetail();
    } catch (error) {
        if (error.response?.status === 403) {
            ElMessage.warning(error.response.data.error || '评论包含敏感词，请修改后重试');
        } else {
            ElMessage.error(error.response?.data?.error || '评论失败');
        }
    } finally {
        submitting.value = false;
    }
};

const goToUserSpace = (id) => {
    if (!id) return;
    router.push(`/user/${id}`);
};

const formatMomentForEditor = (content) => {
    if (!content) return '';
    // Strip image urls from markdown so they don't show up in the text
    let textOnly = content.replace(/!\[.*?\]\((.*?)\)/g, '');
    // Replace hashtags that are preceded by space or start of line
    return textOnly.replace(/(^|\s)(#\S+)/g, '$1<span style="color: #409eff;">$2</span>');
};

const extractMomentImages = (content) => {
    if (!content) return [];
    const imageRegex = /!\[.*?\]\((https?:\/\/[^\s]+)\)/g;
    const images = [];
    let match;
    while ((match = imageRegex.exec(content)) !== null) {
        images.push(match[1]);
    }
    return images;
};

const isAuthor = computed(() => {
    const user = getUser();
    if (!user || !post.value) return false;
    return user.id === post.value.authorId;
});

const canDelete = computed(() => {
    const user = getUser();
    if (!user || !post.value) return false;
    return user.id === post.value.authorId || user.role === 'admin';
});

const handleResolvePost = () => {
    ElMessageBox.confirm('确定将该问题设为已解决吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'success'
    }).then(async () => {
        try {
            await api.put(`/api/forum/posts/${post.value.id}/resolve`);
            ElMessage.success('已标记为已解决');
            fetchPostDetail();
        } catch (error) {
            ElMessage.error('操作失败');
        }
    }).catch(() => { });
};

const handleDeletePost = () => {
    ElMessageBox.confirm('确定要删除这篇帖子吗？此操作不可撤销。', '提示', {
        confirmButtonText: '确定删除',
        cancelButtonText: '取消',
        type: 'warning'
    }).then(async () => {
        try {
            await api.delete(`/api/forum/posts/${post.value.id}`);
            ElMessage.success('帖子已删除');
            router.push('/forum');
        } catch (error) {
            ElMessage.error('删除失败');
        }
    }).catch(() => { });
};

const openEditPostDialog = () => {
    editForm.value.title = post.value.title;
    editForm.value.content = post.value.content;
    editDialogVisible.value = true;
};

const handleUpdatePost = async () => {
    if (!editForm.value.content.trim()) {
        ElMessage.warning('内容不能为空');
        return;
    }
    if (post.value.type !== 'moment' && !editForm.value.title.trim()) {
        ElMessage.warning('标题不能为空');
        return;
    }

    updating.value = true;
    try {
        await api.put(`/api/forum/posts/${post.value.id}`, {
            title: editForm.value.title,
            content: editForm.value.content
        });
        ElMessage.success('帖子更新完成');
        editDialogVisible.value = false;
        fetchPostDetail();
    } catch (error) {
        ElMessage.error(error.response?.data?.error || '更新失败');
    } finally {
        updating.value = false;
    }
};

onMounted(() => {
    fetchPostDetail();
});
</script>

<style scoped>
.post-detail-container {
    max-width: 900px;
    margin: 0 auto;
    padding: 20px;
}

.post-content-container {
    background: transparent;
}

.header {
    margin-bottom: 20px;
}

.post-main-content {
    background: #fff;
    padding: 30px;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
    margin-bottom: 30px;
}

.post-title {
    font-size: 26px;
    color: #303133;
    margin-top: 0;
    margin-bottom: 20px;
}

.post-status-badge {
    margin-bottom: 15px;
}

.post-meta {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.author-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.author-details {
    display: flex;
    flex-direction: column;
}

.author-name {
    font-weight: 500;
    font-size: 15px;
    color: #303133;
}

.post-date {
    font-size: 12px;
    color: #909399;
}

.post-meta-sub {
    display: flex;
    gap: 12px;
    align-items: center;
}

.ip-location {
    font-size: 12px;
    color: #909399;
}

.post-stats {
    font-size: 13px;
    color: #909399;
    display: flex;
    align-items: center;
    gap: 15px;
}

.action-item {
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 4px;
    transition: color 0.2s;
}

.edit-link:hover {
    color: #409eff;
}

.delete-link:hover {
    color: #f56c6c;
}

.post-footer-actions {
    margin-top: 30px;
    text-align: center;
}

.resolve-btn-container {
    padding: 20px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
}

.resolve-btn-container .el-button {
    padding: 15px 40px;
    font-size: 18px;
    height: auto;
}

.resolved-status-footer {
    padding: 20px 0;
    display: flex;
    justify-content: center;
}

.resolved-tag-big {
    padding: 20px 40px;
    font-size: 18px;
    height: auto;
    border-radius: 30px;
    display: flex;
    align-items: center;
}

.resolve-tip {
    font-size: 13px;
    color: #909399;
    margin: 0;
}

.resolve-link {
    color: #67c23a;
}

.resolve-link:hover {
    color: #85ce61;
}

.comments-section {
    background: #fff;
    padding: 30px;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

.comments-header h2 {
    font-size: 20px;
    color: #303133;
    margin-top: 0;
    margin-bottom: 20px;
}

.main-comment-form {
    margin-bottom: 40px;
}

.form-actions {
    margin-top: 15px;
    display: flex;
    justify-content: flex-end;
}

.comments-list {
    margin-top: 20px;
}

.moment-image-grid {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 15px;
    margin-bottom: 20px;
    padding: 0 16px;
    /* align with v-md-editor internal padding */
    max-width: 642px;
}

.moment-image-wrapper {
    width: 200px;
    height: 200px;
    overflow: hidden;
    border-radius: 4px;
    background-color: #f5f7fa;
}

.moment-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

:deep(.v-md-editor-preview) {
    padding: 16px 16px 0;
}

@media screen and (max-width: 768px) {
    .post-detail-container {
        padding: 10px;
    }

    .post-main-content,
    .comments-section {
        padding: 15px;
    }

    .post-title {
        font-size: 20px;
        margin-bottom: 15px;
    }

    .post-meta {
        flex-direction: column;
        align-items: flex-start;
        gap: 15px;
    }

    .post-stats {
        width: 100%;
        justify-content: flex-end;
    }

    .comments-header h2 {
        font-size: 18px;
    }
}
</style>
